import React, { useEffect, useState } from 'react';
import style from './ApplicationForm.style';
import { Alert, Button, FormGroup, Input, Label } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faChevronCircleLeft,
  faChevronCircleRight,
  faCircleDot,
  faExclamationCircle,
  faPencil,
} from '@fortawesome/free-solid-svg-icons';
import RezRateImg from '../../../../../../../assets/images/header/rezRate.gif';
import VerticalStepper from 'Body/RezRATE/Common/VertifcalStepper/VerticalStepper';
import { clone } from 'Services/schedule';
import ApplicationField from '../../../ApplicationField/ApplicationField';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { fetchDataAgnostic, postDataAgnostic, putData } from 'Services/dataApi';
import { formatBearerToken } from 'Common.functions';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { ApplicationFormFields } from 'enums';

import RezRateSideDrawer from 'Body/RezRATE/Common/RezRateSideDrawer/RezRateSideDrawer';
import { RouterPrompt } from 'Body/RezRATE/Common/RouterPrompt/RouterPrompt';
import RezRateBanner from 'Body/RezRATE/Common/RezRateBanner/RezRateBanner';

const statusIcons = {
  'Not Started': faCircleDot,
  'In Progress': faCircleDot,
  Done: faCheckCircle,
};

const statusColors = {
  'Not Started': '#a6a6a6',
  'In Progress': '#FFA500',
  Done: 'green',
};

const ApplicationForm = (props) => {
  const {
    applicationCode,
    applicationDetails,
    selectedOpening,
    applicantDetails,
    isAdmin,
    isReadOnly,
    isLoading,
    onApply,
    onSave,
    isEdit,
    departmentData,
    setExternalHasUnsavedChanges, // TODO! External, for parent
  } = props;
  const { pk_Applicant } = applicantDetails || {};
  const [currentSection, setCurrentSection] = useState();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  const [isSavingForm, setIsSavingForm] = useState(false);
  const [isOpenSideBar, setIsOpenSideBar] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [isLoadingSpecialties, setIsLoadingSpecialties] = useState(false);
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [requiresSpecialtySelection, setRequiresSpecialtySelection] = useState(false);
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState(null);
  const [initialSpecialtyId, setInitialSpecialtyId] = useState(null);

  // Function to get initial sections based on submission status
  const getInitialSections = () => {
    const baseSections = [
      {
        label: 'Personal Information',
        fields: [
          { label: 'First Name', dataKey: 'FirstName', required: true },
          { label: 'Middle Name', dataKey: 'MiddleName', required: false },
          { label: 'Last Name', dataKey: 'LastName', required: true },
          { label: 'Preferred Name', dataKey: 'PreferredName' },
          { label: 'Gender', dataKey: 'Gender' },
          { label: 'Date of Birth', dataKey: 'DateOfBirth' },
          { label: 'Medical License Number', dataKey: 'MedicalLicenseNumber' },
          { label: 'Medical License State', dataKey: 'MedicalLicenseState' },
          { label: 'AAMCID', dataKey: 'AAMCID' },
          { label: 'SFMatch ID', dataKey: 'SFMatchID' },
          { label: 'NRMP ID', dataKey: 'NRMPID' },
        ],
      },
      {
        label: 'Contact Information',
        fields: [
          { label: 'Email*', dataKey: 'Email' },
          { label: 'Phone', dataKey: 'Phone' },
          { label: 'Current Cell Phone Number', dataKey: 'CurrentCellPhoneNumber' },
          { label: 'Current Street Address', dataKey: 'CurrentStreetAddress' },
          { label: 'Current City', dataKey: 'CurrentCity' },
          { label: 'Current State', dataKey: 'CurrentState' },
          { label: 'Current Zip', dataKey: 'CurrentZip' },
          { label: 'Current Country', dataKey: 'CurrentCountry' },
        ],
      },
      {
        label: 'Other Demographic Information',
        fields: [
          { label: 'Country of Birth', dataKey: 'CountryOfBirth' },
          { label: 'City of Birth', dataKey: 'CityOfBirth' },
          { label: 'State of Birth', dataKey: 'StateOfBirth' },
          { label: 'Country of Citizenship', dataKey: 'CountryOfCitizenship' },
          { label: 'Is Visa Required', dataKey: 'IsVisaRequired' },
          { label: 'Will Be Paid By Military', dataKey: 'WillBePaidByMilitary' },
          { label: 'Couples Match', dataKey: 'CouplesMatch', type: 'checkbox' },
          { label: 'Couples Partner Specialty', dataKey: 'CouplesPartnerSpecialty' },
          { label: 'Felony Conviction', dataKey: 'FelonyConviction', type: 'checkbox' },
          { label: 'Felony Conviction Reason', dataKey: 'FelonyConvictionReason' },
        ],
      },
      {
        label: 'USMLE',
        fields: [
          { label: 'USMLE ID', dataKey: 'USMLEID' },
          { label: 'USMLE Step 1 Score', dataKey: 'USMLEStep1Score' },
          { label: 'USMLE Step 1 Status', dataKey: 'USMLEStep1Status' },
          { label: 'USMLE Step 2 CS Score', dataKey: 'USMLEStep2CSScore' },
          { label: 'USMLE Step 2 CS Date', dataKey: 'USMLEStep2CSDate', type: 'date' },
          { label: 'USMLE Step 3 Score', dataKey: 'USMLEStep3Score' },
          { label: 'USMLE Step 3 Date', dataKey: 'USMLEStep3Date', type: 'date' },
          { label: 'USMLE Completion Date', dataKey: 'USMLECompletionDate', type: 'date' },
        ],
      },
      {
        label: 'ABR Core Exam',
        fields: [
          { label: 'Score Pass Fail ABR Core Exam', dataKey: 'ScorePassFailABRCoreExam' },
          {
            label: 'Expected Date Of ABR Core Exam Results',
            dataKey: 'ExpectedDateOfABRCoreExamResults',
            type: 'date',
          },
        ],
      },
      {
        label: 'COMLEX USA',
        fields: [
          { label: 'COMELEX USA Level 1 Date', dataKey: 'COMELEXUSALevel1Date', type: 'date' },
          { label: 'COMELEX USA Level 1 Score', dataKey: 'COMELEXUSALevel1Score' },
          { label: 'COMELEX USA Level 2 CE Date', dataKey: 'COMELEXUSALevel2CEDate', type: 'date' },
          { label: 'COMELEX USA Level 2 CE Score', dataKey: 'COMELEXUSALevel2CEScore' },
          { label: 'COMELEX USA Level 2 PE Date', dataKey: 'COMELEXUSALevel2PEDate', type: 'date' },
          { label: 'COMELEX USA Level 2 PE Score', dataKey: 'COMELEXUSALevel2PEScore' },
          { label: 'COMELEX USA Level 3 Date', dataKey: 'COMELEXUSALevel3Date', type: 'date' },
          { label: 'COMELEX USA Level 3 Score', dataKey: 'COMELEXUSALevel3Score' },
        ],
      },
      {
        label: 'Awards',
        fields: [
          { label: 'Alpha Omega Alpha', dataKey: 'AlphaOmegaAlpha', type: 'checkbox' },
          { label: 'Sigma Sigma Phi', dataKey: 'SigmaSigmaPhi', type: 'checkbox' },
          { label: 'Gold Humanism Honor Society', dataKey: 'GoldHumanismHonorSociety', type: 'checkbox' },
          {
            label: 'Other',
            dataKey: 'OtherAwards',
            type: 'textarea',
          },
        ],
      },
      {
        label: 'Education',
        fields: [
          { label: 'College Attended', dataKey: 'EducationCollegeAttended' },
          { label: 'College Degree', dataKey: 'EducationCollegeDegree' },
          { label: 'College Date Of Graduation', dataKey: 'EducationCollegeDateOfGraduation', type: 'date' },
          { label: 'Medical School Attended', dataKey: 'EducationMedicalSchoolAttended' },
          { label: 'Medical School Degree', dataKey: 'EducationMedicalSchoolDegree' },
          {
            label: 'Medical School Date Of Graduation',
            dataKey: 'EducationMedicalSchoolDateOfGraduation',
            type: 'date',
          },
          { label: 'Place Of Internship', dataKey: 'EducationPlaceOfInternship' },
          { label: 'Internship Specialty', dataKey: 'EducationInternshipSpecialty' },
          { label: 'Internship Start Date', dataKey: 'EducationInternshipStartDate', type: 'date' },
          { label: 'Internship End Date', dataKey: 'EducationInternshipEndDate', type: 'date' },
          { label: 'Place Of Residency', dataKey: 'EducationPlaceOfResidency' },
          { label: 'Residency Specialty', dataKey: 'EducationResidencySpecialty' },
          { label: 'Residency Date Start', dataKey: 'EducationResidencyDateStart', type: 'date' },
          { label: 'Residency Date End', dataKey: 'EducationResidencyDateEnd', type: 'date' },
          { label: 'Place Of Fellowship', dataKey: 'EducationPlaceOfFellowship' },
          { label: 'Fellowship Specialty', dataKey: 'EducationFellowshipSpecialty' },
          { label: 'Fellowship Date Start', dataKey: 'EducationFellowshipDateStart', type: 'date' },
          { label: 'Fellowship Date End', dataKey: 'EducationFellowshipDateEnd', type: 'date' },
          { label: 'Additional Graduate Degree', dataKey: 'AdditionalGraduateDegree' },
          { label: 'Additional Graduate Degree School', dataKey: 'AdditionalGraduateDegreeSchool' },
          {
            label: 'Additional Graduate Degree Date Start',
            dataKey: 'AdditionalGraduateDegreeDateStart',
            type: 'date',
          },
          { label: 'Additional Graduate Degree Date End', dataKey: 'AdditionalGraduateDegreeDateEnd', type: 'date' },
          { label: 'Medical Training Inturupted', dataKey: 'MedicalTrainingInturupted', type: 'checkbox' },
          { label: 'Explanation Of Medical Training Inturupted', dataKey: 'ExplanationOfMedicalTrainingInturupted' },
        ],
      },
      {
        label: 'Publications',
        fields: [
          {
            label: 'Count Of Non-Peer Reviewed Online Publications',
            dataKey: 'CountOfNonPeerReviewedOnlinePublications',
          },
          {
            label: 'Links To Non-Peer Reviewed Online Publications',
            dataKey: 'LinksToNonPeerReviewedOnlinePublications',
          },
          { label: 'Count Of Oral Presentations', dataKey: 'CountOfOralPresentations' },
          { label: 'Count Of Peer Reviewed Book Chapter', dataKey: 'CountOfPeerReviewedBookChapter' },
          {
            label: 'Count Of Peer Reviewed Journal Articles And/or Abstracts',
            dataKey: 'CountOfPeerReviewedJournalArticlesAndOrAbstracts',
          },
          {
            label: 'Count Of Peer Reviewed Journal Articles And/or Abstracts Other Than Published',
            dataKey: 'CountOfPeerReviewedJournalArticlesAndOrAbstractsOtherThanPublished',
          },
          { label: 'Count Of Peer Reviewed Online Publication', dataKey: 'CountOfPeerReviewedOnlinePublication' },
          { label: 'Links To Peer Reviewed Online Publication', dataKey: 'LinksToPeerReviewedOnlinePublication' },
          { label: 'Count Of Poster Presentation', dataKey: 'CountOfPosterPresentation' },
          { label: 'Count Of Scientific Monograph', dataKey: 'CountOfScientificMonograph' },
          { label: 'Count Of Other Articles', dataKey: 'CountOfOtherArticles' },
        ],
      },
      {
        label: 'Hobbies',
        fields: [
          { label: 'Hobbies', dataKey: 'Hobbies', type: 'textarea' },
          { label: 'Other Interests', dataKey: 'OtherInterests', type: 'textarea' },
          { label: 'Language Fluency', dataKey: 'LanguageFluency', type: 'textarea' },
        ],
      },
    ];

    // Only add Review Application section if application is not submitted
    const isSubmitted = applicationDetails?.IsSubmitted;
    if (!isSubmitted) {
      baseSections.push({
        label: 'Review Application',
        fields: [], // Will be populated dynamically
        isReviewSection: true,
      });
    }

    return baseSections;
  };

  const [sections, setSections] = useState(getInitialSections());
  const [bannerMessage, setBannerMessage] = useState('');
  const [bannerColor, setBannerColor] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < window.innerHeight);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [reviewSectionPopulated, setReviewSectionPopulated] = useState(false);

  // Function to populate Review Application section with all fields
  const populateReviewSection = (sectionsArray) => {
    const reviewSectionIndex = sectionsArray.findIndex((section) => section.isReviewSection);
    if (reviewSectionIndex === -1) return sectionsArray;

    const reviewFields = [];

    // Add all fields from all non-review sections
    sectionsArray.forEach((section) => {
      if (!section.isReviewSection) {
        // Add section header
        reviewFields.push({
          label: section.label,
          dataKey: `header_${section.label.replace(/\s+/g, '_')}`,
          isHeader: true,
          type: 'header',
        });

        // Add all fields from this section
        section.fields.forEach((field) => {
          reviewFields.push({
            ...field,
            isReviewMode: true,
            type: 'review',
          });
        });
      }
    });

    // Update the review section with all fields
    const updatedSections = [...sectionsArray];
    updatedSections[reviewSectionIndex] = {
      ...updatedSections[reviewSectionIndex],
      fields: reviewFields,
    };

    return updatedSections;
  };

  const { isAuthenticated, login, getToken } = useKindeAuth();
  // Alert functionality replaced with toast

  useEffect(() => {
    // Your code here
    processSectionStatuses();

    const handleResize = () => {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      if (windowWidth < windowHeight && windowWidth < 680) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      setCurrentSection(null);
    };
  }, []);

  useEffect(() => {
    if (currentSection) {
      setCurrentSectionIndex(sections.findIndex((section) => section.label === currentSection.label));

      processSectionStatuses({ forcedSections: sections });
    }
  }, [currentSection]);

  useEffect(() => {
    // console.log('sections change: ', sections);
  }, [sections]);

  // Populate review section when user navigates to it
  useEffect(() => {
    if (currentSection?.isReviewSection && !reviewSectionPopulated) {
      setSections((prevSections) => {
        const updatedSections = populateReviewSection(prevSections);
        return updatedSections;
      });
      setReviewSectionPopulated(true);
    }
  }, [currentSection, reviewSectionPopulated]);

  useEffect(() => {
    if (
      (applicationCode && !applicationDetails) ||
      (applicationDetails && Object.keys(applicationDetails).length <= 0)
    ) {
      // verifyCode(applicationCode);
    }
  }, [applicationCode]);

  useEffect(() => {
    // THIS IS CORRECT - applicationDetails SHOULD be the submitted form, applicantDetails is the base Application form. IF no submitted form, assume this is a new application and populate sections using base form.
    initializeSections(applicationDetails ? applicationDetails : applicantDetails);
  }, [applicantDetails, applicationDetails]);

  useEffect(() => {
    const departmentId = applicationDetails?.fk_Department || departmentData?.fk_Department;
    const seasonId = applicationDetails?.fk_Season || departmentData?.fk_Season;

    if (!departmentId || !seasonId || !isAuthenticated) {
      return;
    }

    const loadSpecialties = async () => {
      setIsLoadingSpecialties(true);
      try {
        const token = await getToken();
        const response = await fetchDataAgnostic(
          'applicant/application/specialties',
          {
            pk_Department: departmentId,
            pk_Season: seasonId,
          },
          formatBearerToken(token),
        );

        const specialties = response?.data?.specialties || [];
        setSpecialtyOptions(specialties);
        setRequiresSpecialtySelection(!!response?.data?.requiresSelection);

        const defaultSpecialty =
          response?.data?.defaultDepartmentProgramSpecialty?.pk_DepartmentProgramSpecialty || null;
        const existingSpecialty = applicationDetails?.fk_DepartmentProgramSpecialty || null;
        const resolvedSpecialtyId = existingSpecialty || defaultSpecialty || null;
        setSelectedSpecialtyId(resolvedSpecialtyId);
        setInitialSpecialtyId(resolvedSpecialtyId);
      } catch (error) {
        console.log('loadSpecialties err: ', error);
      } finally {
        setIsLoadingSpecialties(false);
      }
    };

    loadSpecialties();
  }, [
    applicationDetails?.fk_Department,
    applicationDetails?.fk_Season,
    applicationDetails?.fk_DepartmentProgramSpecialty,
    departmentData?.fk_Department,
    departmentData?.fk_Season,
    isAuthenticated,
  ]);

  // Update sections when submission status changes
  useEffect(() => {
    const isSubmitted = applicationDetails?.IsSubmitted;
    const hasReviewSection = sections.some((section) => section.isReviewSection);

    // If application is submitted and we still have review section, remove it
    if (isSubmitted && hasReviewSection) {
      setSections((prevSections) => prevSections.filter((section) => !section.isReviewSection));
      // If current section is review section, navigate to first section
      if (currentSection?.isReviewSection) {
        setCurrentSection(sections[0]);
        setCurrentSectionIndex(0);
      }
    }
    // If application is not submitted and we don't have review section, add it
    else if (!isSubmitted && !hasReviewSection) {
      setSections((prevSections) => [
        ...prevSections,
        {
          label: 'Review Application',
          fields: [],
          isReviewSection: true,
        },
      ]);
    }
  }, [applicationDetails?.IsSubmitted]);

  useEffect(() => {
    if (setExternalHasUnsavedChanges) {
      setExternalHasUnsavedChanges(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges]);

  const verifyCode = async (codeToVerify) => {
    const token = await getToken();

    //  For testing, creating app code for Matt's dept.
    postDataAgnostic('applicant/code/verify', null, { code: codeToVerify }, formatBearerToken(token))
      .then((result) => {
        if (result.data && result.data.message && result.data.message === 'Invalid Code') {
          // Invalid Code message. It always throws though, so we never reach here
        } else {
          // history.push(`/applicant/applicant_profile/new_application?applicationCode=${code}`);
        }
      })
      .catch((err) => {
        // setIsValidatingCode(false);
        console.log('err: ', err);
      });
  };

  const autoHideBanner = (time = 3000) => {
    setTimeout(() => {
      setShowBanner(false);
    }, time);
  };

  const handleChange = (data) => {
    const { e, field, fieldIndex } = data;
    const { type, dataKey } = field;
    const { target } = e;

    const initialValue = applicantDetails[dataKey] || '';
    const newSections = sections;
    const currentSectionIndex = newSections.findIndex((section) => section.label === currentSection.label);
    const newSection = newSections[currentSectionIndex];
    const newField = clone(newSection.fields[fieldIndex]);

    switch (type) {
      case 'switch':
        newField.value = target.checked;
        break;
      default:
        newField.value = target.value;
        break;
    }
    newSection.fields[fieldIndex] = newField;
    newSections[currentSectionIndex] = newSection;

    processSectionStatuses({
      forcedSections: newSections,
      callback: (newData) => {
        const { newSections } = newData;
        setSections(newSections);
        setCurrentSection(newSections[currentSectionIndex]);

        if (initialValue !== newField.value) {
          setHasUnsavedChanges(true);
        } else {
          checkForUnsavedChanges(newSections);
        }
      },
    });
  };

  const checkForUnsavedChanges = (sectionsToCheck) => {
    const initialSetup = applicationDetails ? applicationDetails : applicantDetails;
    let newHasUnsavedChanges = false;

    sectionsToCheck.forEach((section) => {
      const { fields = [] } = section;
      fields.forEach((field) => {
        const { value, dataKey } = field;
        const initialValue = initialSetup[dataKey] || '';

        if (initialValue && initialValue !== value) {
          newHasUnsavedChanges = true;
          return;
        }
      });
    });

    setHasUnsavedChanges(newHasUnsavedChanges);
  };

  const processSectionStatuses = (options) => {
    const { callback, forcedSections } = options || {};
    const newSections = clone(forcedSections) || clone(sections);

    newSections.forEach((section, i) => {
      const { status, label, fields = [], isReviewSection } = section;
      const isCurrentSection = currentSection ? currentSection.label === label : false;

      // Skip processing for review sections
      if (isReviewSection) {
        section.status = ' ';
        section.icon = <FontAwesomeIcon icon={faCircleDot} style={{ color: '#a6a6a6', fontSize: 28 }} />;
        return;
      }

      let hasNonEmptyField = false; // for In Progress
      let allRequiredHasContent = true; // for Done(?)
      let allNonRequiredFieldHasContent = true;
      let newStatus = status;

      fields.forEach((field) => {
        const { value, required, label } = field;

        if (field.value) {
          hasNonEmptyField = true;
        } else {
          if (required) {
            allRequiredHasContent = false;
          } else {
            allNonRequiredFieldHasContent = false;
          }
        }
      });

      if (allRequiredHasContent && allNonRequiredFieldHasContent) {
        newStatus = 'Done';
      } else if (hasNonEmptyField) {
        newStatus = 'In Progress';
      }

      section.icon = (
        <FontAwesomeIcon
          icon={statusIcons[newStatus] || faCircleDot}
          style={{ color: statusColors[newStatus], fontSize: 28 }}
        />
      );

      if (!newStatus) {
        section.status = 'Not Started';
      } else {
        section.status = newStatus;
      }
    });

    if (callback) {
      callback({
        newCurrentSection: newSections[0],
        newSections,
      });
    } else {
      if (!currentSection) {
        setCurrentSection(newSections[0]);
      }

      setSections(newSections);
    }
  };

  const initializeSections = (data = {}) => {
    const newSections = clone(sections);

    newSections.forEach((section) => {
      const { fields = [] } = section;
      let hasValue = false;
      let hasAllValues = true;
      fields.forEach((field) => {
        const { dataKey } = field;
        const newValue = data[dataKey] || '';
        field.value = newValue;

        if (newValue) {
          hasValue = true;
        } else {
          hasAllValues = false;
        }
      });
      section.status = hasAllValues ? 'Done' : hasValue ? 'In Progress' : 'Not Started';
    });

    processSectionStatuses({
      forcedSections: newSections,
      callback: (newData) => {
        const { newCurrentSection, newSections } = newData;
        setSections(newSections);
        if (!currentSection) {
          setCurrentSection(() => {
            return newSections[0];
          });
        }

        setHasUnsavedChanges(false);
      },
    });
  };

  const saveForm = async ({ submit = false, doOnApply = false, doSilently = false, doOnSave = false }) => {
    const dataToSubmit = {};
    const sectionsWithMissingRequiredFields = [];

    sections.forEach((section) => {
      const { label, fields, isReviewSection } = section;

      // Skip review sections when saving
      if (isReviewSection) {
        return;
      }

      fields.forEach((field) => {
        const { required, dataKey, value } = field;

        if (submit && required && !value) {
          if (!sectionsWithMissingRequiredFields.includes(label)) {
            sectionsWithMissingRequiredFields.push(label);
          }
        }

        dataToSubmit[dataKey] = value || '';
        if (typeof dataToSubmit[dataKey] === 'string') {
          dataToSubmit[dataKey] = dataToSubmit[dataKey].trim();
        }
      });
    });

    if (sectionsWithMissingRequiredFields.length > 0) {
      if (!doSilently && !isMobile) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          html: `Please fill out all required fields in the following sections:<br>${sectionsWithMissingRequiredFields
            .map((section) => `<br>${section}`)
            .join('')}`,
        });
      } else {
        setBannerColor('#e81300');
        setBannerMessage(
          `Please fill out all required fields in the following sections: ${sectionsWithMissingRequiredFields.join(
            ', ',
          )}`,
        );
        setShowBanner(true);
      }
      return;
    }

    const { fk_Department, fk_Season, fk_ApplicationOpening, pk_ApplicationRecord } =
      applicationDetails || departmentData;
    const selectedSpecialtyIdAsNumber = selectedSpecialtyId ? Number(selectedSpecialtyId) : null;

    if (requiresSpecialtySelection && !selectedSpecialtyIdAsNumber) {
      const specialtyMessage = 'Please select a specialty/track offering before saving.';
      if (!doSilently && !isMobile) {
        Swal.fire({
          icon: 'error',
          title: 'Missing Specialty',
          text: specialtyMessage,
        });
      } else {
        setBannerColor('#e81300');
        setBannerMessage(specialtyMessage);
        setShowBanner(true);
      }
      return;
    }

    const token = await getToken();
    setIsSavingForm(true);
    let promiseToCall;
    if (doSilently) {
      setBannerMessage('Autosaving form. . .');
    } else if (submit) {
      setBannerMessage('Saving and Submitting form. . .');
    } else {
      setBannerMessage('Saving form. . .');
    }

    setBannerColor('#e9ecef');
    setShowBanner(true);

    const hasSpecialtySelectionChanged =
      isEdit && Number(initialSpecialtyId || 0) !== Number(selectedSpecialtyIdAsNumber || 0);

    if (isEdit) {
      if (hasSpecialtySelectionChanged) {
        try {
          await putData(
            'applicant/application',
            { pk_ApplicationRecord: pk_ApplicationRecord },
            {
              fk_DepartmentProgramSpecialty: selectedSpecialtyIdAsNumber,
            },
            formatBearerToken(token),
          );
          setInitialSpecialtyId(selectedSpecialtyIdAsNumber);
        } catch (specialtyUpdateError) {
          setIsSavingForm(false);
          setBannerColor('#e81300');
          setBannerMessage(
            specialtyUpdateError?.response?.data?.message ||
              'Failed to update specialty/track selection. Please try again.',
          );
          setShowBanner(true);
          return;
        }
      }

      promiseToCall = putData(
        'applicant/application/submission',
        { pk_ApplicationRecord: pk_ApplicationRecord },
        { ...dataToSubmit },
        formatBearerToken(token),
      );
    } else {
      promiseToCall = postDataAgnostic(
        'applicant/application',
        {
          pk_Applicant: applicantDetails.pk_Applicant,
          pk_Department: fk_Department,
          pk_Season: fk_Season,
          pk_ApplicationOpening: fk_ApplicationOpening,
          fk_DepartmentProgramSpecialty: selectedSpecialtyIdAsNumber,
        },
        { ...dataToSubmit },
        formatBearerToken(token),
      );
    }

    promiseToCall
      .then((result1) => {
        if (!doSilently && !isMobile) {
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Form saved successfully',
            timer: 3000,
            timerProgressBar: true,
          });
        }

        if (!submit) {
          setShowBanner(true);
          setBannerMessage('Form saved successfully');
          setBannerColor('#38d662');
          autoHideBanner(3000);
        }

        // toast.success('Form saved successfully');
        setIsSavingForm(false);

        if (submit) {
          putData(
            'applicant/application',
            { pk_ApplicationRecord: pk_ApplicationRecord },
            {
              IsSubmitted: true,
            },
            formatBearerToken(token),
          )
            .then((result2) => {
              if (!doSilently && !isMobile) {
                Swal.fire({
                  icon: 'success',
                  title: 'Success',
                  text: 'Form submitted successfully',
                  timer: 3000,
                  timerProgressBar: true,
                });
              }
              // if (window.innerWidth <= 1000) {
              // toast.success('Form saved successfully');
              // setBannerMessage(' ');
              // } else if (doSilently)
              else {
                setBannerMessage('Form submitted successfully');
                setBannerColor('#38d662');
                setShowBanner(true);
                autoHideBanner(3000);
              }
              if (onApply && doOnApply) {
                onApply();
              }
              if (onSave && doOnSave) {
                onSave();
              }

              setHasUnsavedChanges(false);
              setExternalHasUnsavedChanges(false);
            })
            .catch((e) => {
              if (!doSilently && !isMobile) {
                Swal.fire({
                  icon: 'error',
                  title: 'Error',
                  text: 'Form failed to submit',
                });
              }
              //   if (window.innerWidth <= 1000) {
              //   toast.error('Form failed to save');
              // } else if (doSilently)
              else {
                setBannerColor('#e81300');
                setBannerMessage('Form failed to submit');
              }
            });
        } else {
          setHasUnsavedChanges(false);
          setExternalHasUnsavedChanges(false);
          if (onApply && doOnApply) {
            onApply();
          }

          if (onSave && doOnSave) {
            onSave();
          }
        }
      })
      .catch((err) => {
        setIsSavingForm(false);
        if (!doSilently && !isMobile) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Form failed to save',
          });
        }
        //   if (window.innerWidth <= 1000) {
        //   toast.error('Form failed to save');
        // } else if (doSilently)
        else if (!submit) {
          setBannerColor('#e81300');
          setBannerMessage('Form failed to save');
        }

        // toast.error('Form failed to save');
        console.log(' saveForm err: ', err);
      });

    // applicant/application
  };

  const renderSideNavBar = () => {
    const { DepartmentName, DepartmentLogo } = departmentData || {};
    if (isMobile) {
      return (
        <RezRateSideDrawer
          isOpen={isOpenSideBar}
          setIsOpen={setIsOpenSideBar}
          toggle={() => {
            setIsOpenSideBar(!isOpenSideBar);
          }}
        >
          <VerticalStepper
            isMobile={isMobile}
            sections={sections}
            setCurrentSection={setCurrentSection}
            currentSection={currentSection}
            DepartmentLogo={DepartmentLogo}
            onSelect={() => {
              setIsOpenSideBar(false);
              if (hasUnsavedChanges && !isReadOnly && isEdit) {
                saveForm({ doOnApply: false, doSilently: true, doOnSave: true });
              }
            }}
          />
        </RezRateSideDrawer>
      );
    } else {
      return (
        <VerticalStepper
          sections={sections}
          setCurrentSection={setCurrentSection}
          currentSection={currentSection}
          DepartmentLogo={DepartmentLogo}
          onSelect={() => {
            if (hasUnsavedChanges && !isReadOnly && isEdit) {
              saveForm({ doOnApply: false, doSilently: true, doOnSave: true });
            }
          }}
        />
      );
    }
  };

  const renderFormForSection = () => {
    if (!currentSection) {
      return;
    }
    const sectionToDisplay = sections[currentSectionIndex];
    const { label } = sectionToDisplay;

    return (
      <div style={{ ...style.simpleColumn, width: '100%' }}>
        <RezRateBanner message={bannerMessage} backgroundColor={bannerColor} isShow={showBanner} />
        <div
          style={{
            ...style.simpleRow,
            width: '100%',
            marginLeft: 20,
            marginBottom: 10,
            justifyContent: 'space-between',
          }}
        >
          <div style={{ ...style.simpleColumn, width: 250 }}>
            <div style={{ ...style.simpleRow, width: '100%' }}>
              <div style={style.simpleColumn}>
                <div
                  style={{
                    ...style.simpleRow,
                    width: '100%',
                    justifyContent: 'left',
                    paddingLeft: 5,
                    fontSize: 20,
                    fontWeight: 'bold',
                  }}
                >
                  {label}
                </div>
              </div>
            </div>
          </div>
          {isAdmin ||
          isReadOnly ||
          (applicationDetails && (applicationDetails.IsImported || applicationDetails.IsRejected)) ? null : (
            <div style={{ ...style.simpleRow, gap: 10 }}>
              <Button
                size="sm"
                color="primary"
                disabled={isSavingForm || !hasUnsavedChanges || isReadOnly || isLoading}
                style={{ minWidth: 80, fontWeight: 'normal' }}
                onClick={() => {
                  saveForm({ doOnSave: true });
                }}
              >
                Save
              </Button>
              <Button
                size="sm"
                color="success"
                disabled={isSavingForm || isReadOnly || isLoading}
                style={{ minWidth: 80, fontWeight: 'normal' }}
                onClick={() => {
                  Swal.fire({
                    title: 'Save and Submit?',
                    text: 'Applications that are submitted cannot be unsubmitted.',
                    icon: 'warning',
                    showCancelButton: true,
                  }).then((result) => {
                    if (result.isConfirmed) {
                      saveForm({ submit: true, doOnSave: true, doOnApply: true });
                    }
                  });
                }}
              >
                Submit
              </Button>
            </div>
          )}
        </div>

        {specialtyOptions.length > 0 ? (
          <div style={{ width: '100%', paddingLeft: 12, paddingRight: 12, marginBottom: 10 }}>
            {requiresSpecialtySelection ? (
              <Alert color="warning" style={{ marginBottom: 8, paddingTop: 6, paddingBottom: 6 }}>
                Program requires selecting a specialty/track offering before application submission.
              </Alert>
            ) : null}
            <FormGroup style={{ marginBottom: 0 }}>
              <Label style={{ fontWeight: 600 }}>
                Specialty / Track Offering{requiresSpecialtySelection ? ' *' : ''}
              </Label>
              <Input
                type="select"
                value={selectedSpecialtyId || ''}
                disabled={isLoadingSpecialties || isSavingForm || isReadOnly || isLoading}
                onChange={(event) => {
                  const nextValue = event.target.value ? Number(event.target.value) : null;
                  setSelectedSpecialtyId(nextValue);
                  setHasUnsavedChanges(true);
                }}
              >
                <option value="">Select offering</option>
                {specialtyOptions.map((specialty) => {
                  const label =
                    specialty?.DisplayName ||
                    specialty?.SpecialtyKey ||
                    `Offering #${specialty.pk_DepartmentProgramSpecialty}`;
                  return (
                    <option
                      key={specialty.pk_DepartmentProgramSpecialty}
                      value={specialty.pk_DepartmentProgramSpecialty}
                    >
                      {label}
                    </option>
                  );
                })}
              </Input>
            </FormGroup>
          </div>
        ) : null}

        <div
          style={{
            ...style.simpleRow,
            height: 'calc(100vh - 340px)',
            overflowY: 'auto',
            width: '100%',
            alignItems: 'flex-start',
            marginTop: 10,
          }}
        >
          <div style={style.simpleColumn}>
            {sectionToDisplay.fields.map((field, i) => {
              const { dataKey, label, required, value, type, isHeader, isReviewMode } = field;
              const fieldId = `${sectionToDisplay.label}-${dataKey}`;
              const fieldType = type || ApplicationFormFields[dataKey] || 'text';
              return (
                <ApplicationField
                  isMobile={isMobile}
                  key={fieldId}
                  fieldId={fieldId}
                  field={field}
                  type={fieldType}
                  label={label}
                  required={required}
                  value={value}
                  disabled={isReadOnly || isLoading || isSavingForm}
                  isHeader={isHeader}
                  isReviewMode={isReviewMode}
                  onChange={(e) => {
                    if (!isHeader && !isReviewMode) {
                      handleChange({
                        e: e,
                        field: field,
                        fieldIndex: i,
                      });
                    }
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Navigation buttons - Previous/Next only */}
        <div
          style={{
            ...style.simpleRow,
            width: '100%',
            justifyContent: 'center',
            marginTop: 20,
            gap: 20,
          }}
        >
          <Button
            disabled={currentSectionIndex <= 0 || isSavingForm}
            size="sm"
            color="primary"
            style={{ minWidth: 120, width: 120 }}
            onClick={() => {
              if (hasUnsavedChanges && !isReadOnly && isEdit) {
                saveForm({ doOnApply: false, doSilently: true, doOnSave: true });
              }
              setCurrentSection(sections[currentSectionIndex - 1]);
            }}
          >
            <div style={style.simpleRow}>
              <FontAwesomeIcon icon={faChevronCircleLeft} style={{ marginRight: 8 }} />
              Previous
            </div>
          </Button>

          <Button
            disabled={currentSectionIndex >= sections.length - 1 || isSavingForm}
            size="sm"
            color="primary"
            style={{ minWidth: 120, width: 120 }}
            onClick={() => {
              if (hasUnsavedChanges && !isReadOnly && isEdit) {
                saveForm({ doOnApply: false, doSilently: true, doOnSave: true });
              }
              setCurrentSection(sections[currentSectionIndex + 1]);
            }}
          >
            <div style={style.simpleRow}>
              Next
              <FontAwesomeIcon icon={faChevronCircleRight} style={{ marginLeft: 8 }} />
            </div>
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ ...style.simpleRow, width: '100%', alignItems: 'flex-start' }}>
      {renderSideNavBar()} {renderFormForSection()}
      <RouterPrompt
        hasChanges={hasUnsavedChanges}
        okLabel={'Discard Changes'}
        onOk={() => {
          setHasUnsavedChanges(false);

          return true;
        }}
      />
    </div>
  );
};

export default ApplicationForm;
